import { clamp } from '../core/math';
import { SOILS } from './constants';
import type { EarthBalance, Plan, Site } from './types';

const total = (a: readonly number[]): number => a.reduce((x, y) => x + y, 0);

/**
 * 土量バランスと土積曲線。
 * 流用可能率 u と土量変化率 L・C から、残土・購入土・取扱土量 Q・場内の平均運搬距離を出す。
 */
export function earthBalance(
  s: Pick<Site, 'g' | 'gate' | 'area'>,
  p: Pick<Plan, 'soil' | 'usable' | 'cap'>,
): EarthBalance {
  const g = s.g;
  const so = SOILS[p.soil];
  const u = p.usable / 100;
  const { Vc, Vf } = g;
  const needNat = Vf / so.C;
  const usableNat = Vc * u;
  const sur = usableNat - needNat;
  const outNat = Math.max(0, sur) + Vc * (1 - u);
  const borrowNat = Math.max(0, -sur);
  const Q = Math.max(1, Vc + borrowNat);
  const extNat = outNat + borrowNat;
  const extFrac = extNat / Q;
  const outLoose = outNat * so.L;
  const borrowLoose = borrowNat * so.L;
  const n = g.nx;
  const sup: number[] = [];
  const dem: number[] = [];
  for (let i = 0; i < n; i++) {
    sup.push(g.colCut[i]! * u * so.C);
    dem.push(g.colFill[i]!);
  }
  // 余りはゲートの列から搬出、不足はゲートの列へ搬入したことにする
  const gi = clamp(Math.floor((s.gate.x + s.area.w / 2) / g.dx), 0, n - 1);
  const Sx = total(sup) - total(dem);
  const sup2 = sup.slice();
  const dem2 = dem.slice();
  if (Sx > 0) dem2[gi] = dem2[gi]! + Sx;
  else sup2[gi] = sup2[gi]! - Sx;
  let M = 0;
  let Mr = 0;
  let cost = 0;
  const curve: number[] = [];
  const raw: number[] = [];
  for (let i = 0; i < n; i++) {
    Mr += sup[i]! - dem[i]!;
    raw.push(Mr);
    M += sup2[i]! - dem2[i]!;
    curve.push(M);
    cost += Math.abs(M) * g.dx;
  }
  const moved = total(sup2);
  return {
    so,
    u,
    Vc,
    Vf,
    needNat,
    usableNat,
    sur,
    outNat,
    borrowNat,
    Q,
    extNat,
    extFrac,
    outLoose,
    borrowLoose,
    tripsOut: Math.ceil(outLoose / p.cap),
    tripsIn: Math.ceil(borrowLoose / p.cap),
    sup,
    dem,
    raw,
    curve,
    gi,
    avgHaul: moved > 0 ? cost / moved : 0,
  };
}
